import { normalizeDocumentCategories } from 'features/files'
import { useRolodexHistogram } from 'features/rolodex'
import { useMemo } from 'react'
import {
  RolodexEntryTagsType,
  RolodexEntryType,
  RolodexHistogramQuery,
} from 'types'
import z from 'zod'

const categoriesArray = z
  .preprocess((value) => {
    // Rolodex histograms for some reason return string-encoded arrays
    // so weneed to unwrap them before normalizing
    // Try to unwrap JSON-encoded strings first
    let unwrappedValue = value
    if (typeof value === 'string' && value.length > 0) {
      try {
        unwrappedValue = JSON.parse(value)
      } catch {
        // If parsing fails, keep the original string value
        unwrappedValue = value
      }
    }

    if (Array.isArray(unwrappedValue)) {
      return normalizeDocumentCategories(unwrappedValue)
    }
    if (typeof unwrappedValue === 'string' && unwrappedValue.length > 0) {
      return normalizeDocumentCategories([unwrappedValue])
    }
    return []
  }, z.array(z.string()))
  .default([])

const categoriesSchema = z
  .object({
    categories: categoriesArray,
  })
  .passthrough()

export function useExistingDocumentCategories(
  tags: RolodexEntryTagsType | undefined
): {
  categories: ReadonlyArray<string>
  isLoading: boolean
  forceRefresh: () => Promise<void>
} {
  const { data, isLoading, forceRefresh } = useRolodexHistogram(
    useMemo<RolodexHistogramQuery | undefined>(
      () =>
        tags
          ? {
              type: RolodexEntryType.REPORT,
              tags,
              select: [{ key: 'categories', label: 'categories' }],
              filters: [{ key: 'categories', op: 'notnull' }],
              limit: 10000,
            }
          : undefined,
      [tags]
    )
  )
  return useMemo(() => {
    const flattened = data
      ?.flat()
      .map((record) => categoriesSchema.parse(record).categories)
      .flat()

    return {
      categories: normalizeDocumentCategories(flattened ?? []),
      isLoading,
      forceRefresh,
    }
  }, [data, forceRefresh, isLoading])
}
